import React, { useRef } from 'react';
import {
  Alert,
  Button,
  CheckCircleIcon,
  Text,
  UploadFileIcon,
} from '@billda/design-system';

interface FileUploadStepProps {
  onFileSelected: (file: File) => void;
  loading: boolean;
  selectedFile: File | null;
  error?: string | null;
}

const requirements = [
  {
    primary: 'Must be a CSV file (.csv extension)',
    secondary: 'Contains plot and labour item data',
  },
  {
    primary: 'Proper column headers',
    secondary:
      'Add one CONFIGURATION row after the headers to set Fixed amount or a rate such as £20 per m²',
  },
  {
    primary: 'Missing labour items will be identified',
    secondary: 'You can create them during the preview step',
  },
];

const FileUploadStep: React.FC<FileUploadStepProps> = ({
  onFileSelected,
  loading,
  selectedFile,
  error,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onFileSelected(file);
  };

  return (
    <div
      className="rounded-xl border border-border-default bg-surface-default p-6"
      data-testid="file-upload-step"
    >
      <Text variant="h6" className="mb-2" data-testid="file-upload-header">
        Upload CSV File
      </Text>
      <Text variant="body2" color="secondary" className="mb-6">
        Select a CSV file containing your site data. The file will be validated
        and previewed before import.
      </Text>

      {/* File Selection Button */}
      <div className="mb-6">
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          ref={fileInputRef}
          className="hidden"
          data-testid="file-upload-input"
        />

        <Button
          variant="neutral"
          loading={loading}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
          size="lg"
          data-testid="file-upload-button"
        >
          {loading ? null : (
            <UploadFileIcon className="size-4" aria-hidden="true" />
          )}
          {loading ? 'Processing...' : 'Select CSV File'}
        </Button>

        {selectedFile && !loading && (
          <Text
            variant="body2"
            color="secondary"
            className="mt-2"
            data-testid="file-selected-info"
          >
            Selected: {selectedFile.name}
          </Text>
        )}
      </div>

      {/* File Format Information */}
      <div className="mb-6" data-testid="file-upload-requirements">
        <Text variant="body2" className="mb-2 font-semibold">
          CSV File Requirements:
        </Text>
        <div>
          {requirements.map(requirement => (
            <div
              key={requirement.primary}
              className="flex items-start gap-3 py-1.5"
            >
              <CheckCircleIcon
                className="mt-0.5 size-4 shrink-0 text-content-success"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <Text variant="body2" className="text-content-primary">
                  {requirement.primary}
                </Text>
                <Text variant="caption" color="secondary" className="block">
                  {requirement.secondary}
                </Text>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          className="mb-4"
          data-testid="file-upload-error"
        >
          <Text variant="body2">{error}</Text>
        </Alert>
      )}
    </div>
  );
};

export default FileUploadStep;
